import { useState } from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Lock, LockOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SectionHeading from "@/components/SectionHeading";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";

const DEFAULT_QUESTION = "Di chi è la massima «M'è sfrantecat 'o cazz»?";

export default function ClubShop() {
  const { clubUnlocked, unlockClub } = useCart();
  const { data: gate } = useQuery({
    queryKey: ["club-question"],
    queryFn: async () => (await api.get("/club/question")).data,
    staleTime: 60_000,
  });
  const question = gate?.question || DEFAULT_QUESTION;
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("nfc_club_products")) || [];
    } catch {
      return [];
    }
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/club/unlock", { answer });
      unlockClub();
      setProducts(data.products);
      sessionStorage.setItem("nfc_club_products", JSON.stringify(data.products));
      toast.success("Risposta esatta. Benvenuto nella zona riservata ai soci!");
    } catch {
      toast.error("Risposta sbagliata. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="ordini-club" data-testid="club-shop-section" className="py-24 sm:py-32 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          overline="Ordini del Club"
          title="La zona riservata ai soci"
          description="Tutto il merch del Neapolis Free Chapter, più gli articoli esclusivi visibili solo agli appartenenti al club."
          testid="club-shop-heading"
        />

        {!clubUnlocked ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="mt-12 max-w-xl mx-auto leather-card p-8 text-center"
            data-testid="club-gate"
          >
            <div className="w-14 h-14 mx-auto border border-[#C5A059]/40 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#C5A059]" />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold text-[#F4EBE1]">
              Sei dei nostri?
            </h3>
            <p className="mt-2 text-sm text-[#C0B9B0]" data-testid="club-gate-question">{question}</p>
            <form onSubmit={submit} className="mt-6 flex gap-2">
              <input
                data-testid="club-answer-input"
                type="text"
                required
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Scrivi qui la risposta..."
                className="flex-1 bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3.5 text-sm text-[#F4EBE1] transition-colors"
              />
              <button
                data-testid="club-unlock-button"
                type="submit"
                disabled={loading}
                className="px-6 py-3.5 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entra"}
              </button>
            </form>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D]">
              Solo i soci conoscono la risposta
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12"
            data-testid="club-unlocked-area"
          >
            <div className="flex items-center gap-3 mb-8">
              <LockOpen className="w-5 h-5 text-[#C5A059]" />
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C5A059]">
                Zona soci sbloccata — gli articoli con il badge "Solo Soci" sono riservati al club
              </p>
            </div>
            {products.length === 0 ? (
              <p className="text-sm text-[#78746D] font-mono">
                Nessun prodotto disponibile al momento.
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="club-products-grid">
                {products.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
}
